import { cn } from '../../lib/cn';
import { useInViewOnce } from '../../hooks/useInViewOnce';
import styles from './Reveal.module.css';

/**
 * A stable, un-hashed marker so other CSS modules can react to a reveal
 * without importing this one — CSS Modules hashes `.in`, which makes it
 * unreachable from a sibling stylesheet.
 */
export const REVEALED = 'is-revealed';

/**
 * The one scroll-reveal wrapper. Replaces the byte-identical containerVariants /
 * itemVariants objects that were copy-pasted into all four of the old pages.
 *
 * Deliberately CSS transitions rather than framer-motion — see Reveal.module.css
 * for why. `immediate` skips the observer for above-the-fold content, which has
 * nothing to scroll into view.
 */
export function Reveal({
  as: Tag = 'div',
  variant,          // 'fade' | 'left' | 'right' | 'scale'; default is a rise
  delay = 0,        // seconds
  y,                // px override for the default rise
  immediate = false,
  className,
  style,
  children,
  ...rest
}) {
  const [ref, inView] = useInViewOnce();
  const visible = immediate || inView;

  return (
    <Tag
      ref={ref}
      className={cn(styles.reveal, variant && styles[variant], visible && styles.in, visible && REVEALED, className)}
      style={{
        '--reveal-delay': delay ? `${delay}s` : undefined,
        '--reveal-y': y != null ? `${y}px` : undefined,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Reveals its children in sequence. The stagger is CSS nth-child, so items do
 * not need to know their own index.
 */
export function RevealGroup({ as: Tag = 'div', immediate = false, stagger, className, style, children, ...rest }) {
  const [ref, inView] = useInViewOnce();
  const visible = immediate || inView;

  return (
    <Tag
      ref={ref}
      className={cn(styles.group, visible && styles.in, visible && REVEALED, className)}
      style={{ '--stagger': stagger != null ? `${stagger}ms` : undefined, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** A child of RevealGroup. Transitions in once the group becomes visible. */
export function RevealItem({ as: Tag = 'div', variant, y, className, style, children, ...rest }) {
  return (
    <Tag
      className={cn(styles.reveal, variant && styles[variant], className)}
      style={{ '--reveal-y': y != null ? `${y}px` : undefined, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
